import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { galleryImages } from '../data/mock';
import { Maximize2 } from 'lucide-react';
import SEO from '../components/SEO';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showBefore, setShowBefore] = useState(true);

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": "Before & After Gallery | Pierce Premier Cleaning Services",
        "description": "View our professional cleaning results in Brevard County",
        "breadcrumb": {
          "@id": "https://pierce-premier.preview.emergentagent.com/gallery#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://pierce-premier.preview.emergentagent.com/gallery#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://pierce-premier.preview.emergentagent.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Gallery"
          }
        ]
      }
    ]
  };

  return (
    <>
      <SEO 
        title="Before & After Gallery | Pierce Premier Cleaning Services"
        description="See the transformation our professional cleaning delivers in Brevard County homes and businesses. Real before and after photos from actual cleaning projects."
        canonical="/gallery"
        structuredData={structuredData}
      />
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-7xl text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Before & After Gallery
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            See the transformation our professional cleaning services deliver
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-2 gap-8">
            {galleryImages.map((image) => (
              <Card key={image.id} className="group overflow-hidden hover:shadow-2xl transition-all duration-300">
                <CardContent className="p-0">
                  <div className="grid grid-cols-2">
                    {/* Before Image */}
                    <div className="relative overflow-hidden">
                      <Badge className="absolute top-4 left-4 z-10 bg-red-500 text-white">
                        Before
                      </Badge>
                      <img
                        src={image.before}
                        alt={`${image.title} - Before`}
                        className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    </div>

                    {/* After Image */}
                    <div className="relative overflow-hidden">
                      <Badge className="absolute top-4 right-4 z-10 bg-green-500 text-white">
                        After
                      </Badge>
                      <img
                        src={image.after}
                        alt={`${image.title} - After`}
                        className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <button
                        onClick={() => setSelectedImage(image)}
                        className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm p-2 rounded-lg hover:bg-white transition-colors"
                      >
                        <Maximize2 className="w-5 h-5 text-gray-900" />
                      </button>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="p-6 bg-gradient-to-r from-cyan-50 to-blue-50">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {image.title}
                    </h3>
                    <p className="text-gray-600">
                      {image.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Info Section */}
          <div className="mt-16 bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Your Space Could Be Next!
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience the same level of transformation. Our professional team uses the best equipment and techniques to deliver outstanding results every time.
            </p>
          </div>
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {selectedImage?.title}
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="space-y-4">
              <div className="flex justify-center space-x-4 mb-4">
                <button
                  onClick={() => setShowBefore(true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    showBefore
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Before
                </button>
                <button
                  onClick={() => setShowBefore(false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    !showBefore
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  After
                </button>
              </div>
              <div className="relative rounded-lg overflow-hidden">
                <img
                  src={showBefore ? selectedImage.before : selectedImage.after}
                  alt={`${selectedImage.title} - ${showBefore ? 'Before' : 'After'}`}
                  className="w-full h-[600px] object-cover"
                />
                <Badge className={`absolute top-4 ${showBefore ? 'left-4 bg-red-500' : 'right-4 bg-green-500'} text-white text-lg px-4 py-2`}>
                  {showBefore ? 'Before' : 'After'}
                </Badge>
              </div>
              <p className="text-center text-gray-600">
                {selectedImage.description}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default GalleryPage;
